/**
 * RecruiterSpeedReport — "Recruiter speed to lead" at the top of Reports →
 * Metrics (owner request 2026-10-01). How long a PERSON took to reach an
 * eligible or needs-review lead once it was the team's turn: from the form
 * (or, for a lead the texting agent took, from the agent's hand-off; or from a
 * call time the lead booked) to the first call, text or email a team member
 * sent, or the lead's call a team member answered. The welcome text, flows,
 * the agent and reminders never count. Two measures from one read: business
 * hours (Settings → Booking) and all hours; the toggle is a local preference.
 *
 * Rules: crm.recruiter_speed_stats (backend/db/core/migrations/
 * 20261001120000_crm_recruiter_speed.sql). Formatting: recruiterSpeedLogic.ts.
 */
import { useEffect, useState } from 'react';
import { Timer } from 'lucide-react';
import api from '../../../services/api';
import type { RecruiterSpeed, RecruiterSpeedRow, SpeedMeasure, SpeedStats } from '../../types';
import { getPref, setPref } from '../../lib';
import SegmentedControl from '../filters/SegmentedControl';
import { SummaryStat } from './StatBlocks';
import {
  DEFAULT_SPEED_MODE,
  SPEED_MODE_OPTIONS,
  SPEED_MODE_PREF,
  SPEED_THRESHOLD_KEYS,
  asideLine,
  fmtShare,
  fmtWait,
  isSpeedMeasure,
  modeNote,
  pathHint,
  pathLabel,
  recruiterLabel,
  studyLabel,
  thresholdLabel,
} from './recruiterSpeedLogic';

interface Props {
  from: string | null;
  to: string | null;
  studyId?: string | null;
}

type GroupKey = 'recruiter' | 'study';

const thCls = 'whitespace-nowrap px-3 py-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';
const tdNum = 'whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100';

function readMode(): SpeedMeasure {
  const v = getPref(SPEED_MODE_PREF, DEFAULT_SPEED_MODE);
  return isSpeedMeasure(v) ? v : DEFAULT_SPEED_MODE;
}

function PathCard({ stats, path, mode }: { stats: SpeedStats; path: 'web' | 'agent'; mode: SpeedMeasure }) {
  const m = stats[mode];
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-baseline justify-between gap-x-2">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{pathLabel(path)}</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">{pathHint(path)}</span>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-600 dark:text-gray-300">
        <span>
          Median <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">{fmtWait(m.median_sec, mode)}</span>
        </span>
        <span>
          p90 <span className="tabular-nums">{fmtWait(m.p90_sec, mode)}</span>
        </span>
        <span>
          {thresholdLabel('1h', mode)} <span className="tabular-nums">{fmtShare(m.within['1h'])}</span>
        </span>
        <span className="tabular-nums">
          {stats.leads.toLocaleString()} lead{stats.leads === 1 ? '' : 's'} · {stats.waiting.toLocaleString()} not reached
        </span>
        {path === 'agent' && stats.with_agent > 0 && (
          <span className="tabular-nums">{stats.with_agent.toLocaleString()} not handed over</span>
        )}
      </div>
    </div>
  );
}

function GroupTable({ rows, group, mode }: { rows: RecruiterSpeedRow[]; group: GroupKey; mode: SpeedMeasure }) {
  if (!rows.length) {
    return <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No leads to break down in this range.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800/50">
          <tr>
            <th scope="col" className={`${thCls} text-left`}>{group === 'recruiter' ? 'Recruiter' : 'Study'}</th>
            <th scope="col" className={`${thCls} text-right`}>Leads</th>
            <th scope="col" className={`${thCls} text-right`}>Reached</th>
            <th scope="col" className={`${thCls} text-right`}>Median</th>
            <th scope="col" className={`${thCls} text-right`}>p90</th>
            <th scope="col" className={`${thCls} text-right`}>{thresholdLabel('1h', mode)}</th>
            <th scope="col" className={`${thCls} text-right`}>{thresholdLabel('1d', mode)}</th>
            <th scope="col" className={`${thCls} text-right`}>Not reached yet</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {rows.map((r) => {
            const m = r[mode];
            const label = group === 'recruiter' ? recruiterLabel(r) : studyLabel(r);
            return (
              <tr key={r.key ?? '__none'} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className={`max-w-[14rem] truncate px-3 py-2 ${r.name ? 'text-gray-900 dark:text-gray-100' : 'italic text-gray-500 dark:text-gray-400'}`} title={label}>
                  {label}
                </td>
                <td className={tdNum}>{r.leads.toLocaleString()}</td>
                <td className={tdNum}>{r.reached.toLocaleString()}</td>
                <td className={`${tdNum} font-semibold`}>{fmtWait(m.median_sec, mode)}</td>
                <td className={tdNum}>{fmtWait(m.p90_sec, mode)}</td>
                <td className={tdNum}>{fmtShare(m.within['1h'])}</td>
                <td className={tdNum}>{fmtShare(m.within['1d'])}</td>
                <td
                  className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${r.waiting > 0 ? 'font-medium text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}
                  title={r.waiting > 0 ? `Longest wait ${fmtWait(m.oldest_wait_sec, mode)}` : undefined}
                >
                  {r.waiting.toLocaleString()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function RecruiterSpeedReport({ from, to, studyId = null }: Props) {
  const [data, setData] = useState<RecruiterSpeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<SpeedMeasure>(readMode);
  const [group, setGroup] = useState<GroupKey>('recruiter');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get('/api/crm/analytics/recruiter-speed', {
        params: { from: from || undefined, to: to || undefined, study_id: studyId || undefined },
      })
      .then((res) => {
        if (!alive) return;
        setData(res.data || null);
        setError(null);
      })
      .catch((err) => {
        if (alive) setError(err?.response?.data?.error || 'Failed to load recruiter speed to lead');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [from, to, studyId]);

  const pickMode = (v: string) => {
    if (!isSpeedMeasure(v) || v === mode) return;
    setMode(v);
    setPref(SPEED_MODE_PREF, v);
  };

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
      <div className="min-w-0">
        <h2 id="recruiter-speed-title" className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <Timer className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Recruiter speed to lead
        </h2>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          Eligible and needs-review leads that came in during this range: the time from the team’s turn to the first call,
          text or email from a person.
        </p>
      </div>
      <SegmentedControl label="Measure speed in" value={mode} onChange={pickMode} options={SPEED_MODE_OPTIONS} />
    </div>
  );

  let body: JSX.Element;
  if (loading && !data) {
    body = <p className="p-1 text-sm text-gray-400">Loading recruiter speed…</p>;
  } else if (error && !data) {
    body = <p className="p-1 text-sm text-red-600 dark:text-red-400">{error}</p>;
  } else if (!data || (data.overall.leads === 0 && data.overall.with_agent === 0 && data.overall.booked_later === 0)) {
    body = (
      <p className="p-1 text-sm text-gray-500 dark:text-gray-400">
        No eligible or needs-review leads came in during this range.
      </p>
    );
  } else {
    const o = data.overall;
    const m = o[mode];
    const aside = asideLine(o);
    const showStudy = !data.study_scoped && data.by_study.length > 0;
    const activeGroup: GroupKey = showStudy ? group : 'recruiter';
    body = (
      <div className="space-y-3">
        <p className="text-xs text-gray-500 dark:text-gray-400">{modeNote(mode, data.hours)}</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          <SummaryStat
            label="Median time to reach"
            value={fmtWait(m.median_sec, mode)}
            sub={`p90 ${fmtWait(m.p90_sec, mode)} · ${o.reached.toLocaleString()} of ${o.leads.toLocaleString()} reached`}
          />
          {SPEED_THRESHOLD_KEYS.map((k) => (
            <SummaryStat
              key={k}
              label={thresholdLabel(k, mode)}
              value={fmtShare(m.within[k])}
              sub={m.within[k].of ? `${m.within[k].hit.toLocaleString()} of ${m.within[k].of.toLocaleString()} leads` : 'none due yet'}
            />
          ))}
          <SummaryStat
            label="Not reached yet"
            value={o.waiting.toLocaleString()}
            sub={o.waiting ? `longest wait ${fmtWait(m.oldest_wait_sec, mode)}` : 'nobody waiting'}
          />
        </div>
        {aside && <p className="px-1 text-xs text-gray-500 dark:text-gray-400">{aside}</p>}

        <div className="grid gap-3 lg:grid-cols-2">
          {data.by_path.map((p) => (
            <PathCard key={p.key} stats={p} path={p.key} mode={mode} />
          ))}
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
              {activeGroup === 'recruiter' ? 'By recruiter (the lead’s recruiter for that study)' : 'By study (the study of the form)'}
            </span>
            {showStudy && (
              <SegmentedControl
                label="Break down by"
                value={activeGroup}
                onChange={(v) => setGroup(v === 'study' ? 'study' : 'recruiter')}
                options={[
                  { value: 'recruiter', label: 'By recruiter' },
                  { value: 'study', label: 'By study' },
                ]}
              />
            )}
          </div>
          <GroupTable rows={activeGroup === 'recruiter' ? data.by_recruiter : data.by_study} group={activeGroup} mode={mode} />
        </div>

        <p className="px-1 text-[11px] leading-snug text-gray-400">
          How it is counted: a lead is one eligible or needs-review form. The clock starts when the form comes in; for
          a lead the texting agent took, when the agent hands it to the team (review ready, they ask for a call or a
          person, someone takes over, or the agent is paused or stopped); and at the time they booked if they booked a call themselves. It stops at the
          first call, text or email a team member sends, or when a team member answers their call. Shares count a lead
          still not reached once the time has passed as late; one closed before then is left out. The same study’s form
          sent again within {data.rejoin_hours.toLocaleString()} hours while nobody has reached out stays on the first
          form’s clock.
        </p>
      </div>
    );
  }

  return (
    <section aria-labelledby="recruiter-speed-title" className="space-y-3">
      {header}
      {body}
    </section>
  );
}
